import { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Lock, User } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ROUTES } from "@/config/constants";
import { defaultRouteAfterLogin, isMarketingOnlyUser, isOperadorOnlyUser } from "@/lib/appAccess";
import { useAppAccess } from "@/hooks/useAppAccess";

export function LoginPage() {
  const { login, isAuthenticated, loading, error, user } = useAuth();
  const { can } = useAppAccess();
  const navigate  = useNavigate();
  const location  = useLocation();
  const fromPath  = (location.state as { from?: Location })?.from?.pathname ?? ROUTES.HOME;

  const [username,    setUsername]    = useState("");
  const [password,    setPassword]    = useState("");
  const [showPass,    setShowPass]    = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const usernameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    usernameRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const operadorOnly = isOperadorOnlyUser(can, {
      locacoesOperador: user.locacoesOperador,
      role: user.role,
    });
    const marketingOnly = isMarketingOnlyUser(can, {
      locacoesOperador: user.locacoesOperador,
      role: user.role,
    });
    const homeDefault = defaultRouteAfterLogin(can, {
      locacoesOperador: user.locacoesOperador,
      role: user.role,
    });
    let target = fromPath === ROUTES.HOME || fromPath === ROUTES.LOGIN ? homeDefault : fromPath;
    if (operadorOnly && !target.startsWith(ROUTES.OPERADOR)) {
      target = ROUTES.OPERADOR;
    }
    if (marketingOnly && !target.startsWith(ROUTES.MARKETING)) {
      target = ROUTES.MARKETING;
    }
    navigate(target, { replace: true });
  }, [isAuthenticated, user, can, fromPath, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setSubmitting(true);
    await login(username.trim(), password);
    setSubmitting(false);
  };

  const isLoading = loading || submitting;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">

        {/* Card principal */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card px-8 py-10">

          {/* Logo / Brand */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-600 mb-4 shadow-lg">
              <svg viewBox="0 0 24 24" className="w-7 h-7 text-white fill-current">
                <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-slate-800 tracking-tight">
              BI SEMOG Engenharia
            </h1>
            <p className="text-xs font-semibold text-brand-600 uppercase tracking-widest mt-1">
              Painel Gerencial
            </p>
          </div>

          {/* Formulário */}
          <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
            <Input
              ref={usernameRef}
              label="Usuário"
              type="text"
              placeholder="seu.usuario"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
              leftIcon={<User className="w-4 h-4" />}
            />

            <Input
              label="Senha"
              type={showPass ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              leftIcon={<Lock className="w-4 h-4" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                  tabIndex={-1}
                  aria-label={showPass ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showPass ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              }
            />

            {/* Mensagem de erro */}
            {error && (
              <div
                role="alert"
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 font-medium"
              >
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={isLoading}
              disabled={!username.trim() || !password}
              className="w-full mt-2"
            >
              Entrar
            </Button>
          </form>
        </div>

        {/* Rodapé */}
        <p className="text-center text-xs text-slate-400 mt-6">
          SEMOG Engenharia &copy; {new Date().getFullYear()} &middot; Todos os direitos reservados
        </p>
      </div>
    </div>
  );
}
